import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '60vh', padding: '2rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '2rem',
            maxWidth: '480px', width: '100%', textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>!</div>
            <h2 style={{ margin: '0 0 0.5rem', color: '#1a1a1a', fontSize: '1.25rem' }}>
              Something went wrong
            </h2>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details style={{ textAlign: 'left', marginBottom: '1rem' }}>
                <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.8rem' }}>
                  Error details
                </summary>
                <pre style={{
                  background: '#f5f5f5', padding: '0.75rem', borderRadius: '6px',
                  fontSize: '0.75rem', overflow: 'auto', maxHeight: '120px',
                  color: '#c00', marginTop: '0.5rem',
                }}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                style={{
                  background: '#2563eb', color: '#fff', border: 'none',
                  padding: '0.6rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                Reload
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#fff', color: '#2563eb', border: '1px solid #2563eb',
                  padding: '0.6rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
